/**
 * TimeBars - a report builder result over time, as vertical CSS bars (docs/plans/crm-report-builder-search-
 * collab-plan.md A4.2, A4.3 step 2, B4.6, decision R4 a; work package R-7). No chart library: the
 * stacked-height pattern of components/reports/ScreeningGateReport.tsx (a fixed-height box per bucket,
 * the bar a percentage of the tallest, its segments percentages of the bar), from builderLogic
 * timeBars over a pivot whose columns are the time buckets.
 *
 * Every bucket of the range is drawn, the empty ones too (a chart that skipped a quiet week would lie),
 * and past 31 buckets the bars keep their width and the chart scrolls sideways.
 *
 * Stacking adds a bucket's groups together, which is only true when each lead or event counts once
 * (an additive measure). A distinct count ("Leads moved", "Leads worked", "Reached each stage") can
 * count one lead in several groups, so with more than one group it is drawn as one row of bars per
 * group, one under another, on one scale: never stacked into a total that does not exist.
 *
 * Colors: "Other" and "(none)" grey, a stage or status its lifecycle data color, other groups a fixed
 * series palette in the table's order; a legend names them.
 */
import { Z } from '../components/ui';
import { lifecycleMeta } from '../lib';
import { displayValue, timeBars } from './builderLogic';
import type { PivotHeader, PivotTable } from './builderLogic';
import type { ReportUnit } from './types';

/** Past this many buckets the bars keep a fixed width and the chart scrolls sideways (plan B4.6). */
export const SCROLL_PAST = 31;
/** When the bars share the width, at most this many date labels are written under them. */
const LABELS_WHEN_FITTED = 12;
/** The bar box's height in pixels (ScreeningGateReport's 124). */
const BAR_BOX = 124;

/** Distinct data colors for groups, in the table's order (written out: Tailwind cannot see built names). */
const SERIES: readonly string[] = [
  'bg-primary-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-teal-500',
  'bg-rose-500',
  'bg-lime-500',
  'bg-indigo-400',
  'bg-orange-500',
  'bg-sky-300',
  'bg-fuchsia-500',
  'bg-emerald-600',
  'bg-yellow-400',
  'bg-violet-300',
  'bg-red-700',
  'bg-cyan-800',
];
const OTHER_COLOR = 'bg-gray-400 dark:bg-gray-500';
const NONE_COLOR = 'bg-gray-300 dark:bg-gray-600';

export interface TimeBarsProps {
  /** A pivot whose columns are time buckets (builderLogic pivot with a time split). */
  table: PivotTable;
  /** What is drawn ("New leads by week"), for screen readers. */
  title: string;
}

/** Each row's color: grey for "Other" and "(none)", the lifecycle color for a stage, else the series. */
export function seriesColors(table: PivotTable): Map<string, string> {
  const colors = new Map<string, string>();
  let next = 0;
  for (const row of table.rows) {
    if (row.isOther) colors.set(row.key, OTHER_COLOR);
    else if (row.isNone) colors.set(row.key, NONE_COLOR);
    else if (table.rowAxis.kind === 'status') colors.set(row.key, lifecycleMeta(row.key).dot);
    else {
      colors.set(row.key, SERIES[next % SERIES.length]);
      next += 1;
    }
  }
  return colors;
}

/** True when the rows can be stacked into one bar per bucket: an additive measure, or one row only. */
export function stackable(table: PivotTable): boolean {
  return table.additive || table.rows.length <= 1;
}

const numberText = (value: number, unit: ReportUnit): string =>
  displayValue(value, unit).toLocaleString('en-US', { maximumFractionDigits: 1 });

/** Which bucket labels to write: all when scrolling, else about a dozen spread across. */
const labelStep = (count: number, scroll: boolean): number => (scroll ? 1 : Math.max(1, Math.ceil(count / LABELS_WHEN_FITTED)));

function Legend({ table, colors }: { table: PivotTable; colors: Map<string, string> }) {
  if (table.rows.length <= 1 || table.rowAxis.kind === 'measure') return null;
  return (
    <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1" aria-label="Legend" data-rb="legend">
      {table.rows.map((row) => (
        <li
          key={row.key}
          className={`inline-flex items-center gap-1 text-[11px] ${
            row.isOther || row.isNone ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-sm ${colors.get(row.key) ?? OTHER_COLOR}`} />
          {row.label}
        </li>
      ))}
    </ul>
  );
}

/** One stacked bar per bucket. */
function Stacked({ table, title, colors }: TimeBarsProps & { colors: Map<string, string> }) {
  const bars = timeBars(table);
  const scroll = bars.length > SCROLL_PAST;
  const step = labelStep(bars.length, scroll);
  const width = scroll ? 'w-7 flex-shrink-0' : 'min-w-0 flex-1';
  return (
    <div className="overflow-x-auto pb-1">
      <ul aria-label={title} data-rb="time-bars" className="flex items-end gap-1" style={{ minHeight: BAR_BOX + 36 }}>
        {bars.map((b, i) => {
          const total = numberText(b.total, table.unit);
          return (
            <li
              key={b.key}
              data-rb-bar={b.key}
              aria-label={`${b.long}: ${total}`}
              title={`${b.long}: ${total}`}
              className={`flex flex-col items-center gap-1 ${width}`}
            >
              <div className="flex w-full max-w-[2.5rem] flex-col justify-end" style={{ height: BAR_BOX }}>
                <div className="flex w-full flex-col-reverse overflow-hidden rounded-t-sm" style={{ height: `${b.heightPct}%` }}>
                  {b.segments.map((s) => (
                    <div
                      key={s.key}
                      data-rb-segment={s.key}
                      className={colors.get(s.key) ?? OTHER_COLOR}
                      style={{ height: `${s.pct}%` }}
                      title={`${s.label}: ${numberText(s.value, table.unit)}`}
                    />
                  ))}
                </div>
              </div>
              <span data-rb-bar-total={b.key} className="text-[10px] font-medium leading-none tabular-nums text-gray-600 dark:text-gray-300">
                {total}
              </span>
              <span
                aria-hidden="true"
                className={`whitespace-nowrap text-[9px] leading-none text-gray-400 dark:text-gray-500 ${i % step === 0 ? '' : 'invisible'}`}
              >
                {b.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** One row of bars per group, one scale for all (a distinct count is never stacked). */
function Rows({ table, title, colors }: TimeBarsProps & { colors: Map<string, string> }) {
  const scroll = table.columns.length > SCROLL_PAST;
  const step = labelStep(table.columns.length, scroll);
  const width = scroll ? 'w-6 flex-shrink-0' : 'min-w-0 flex-1';
  const labelCell = `sticky left-0 ${Z.rail} w-32 flex-shrink-0 truncate bg-white pr-2 text-xs dark:bg-gray-800 sm:w-40`;
  const quiet = (h: PivotHeader) => h.isOther || h.isNone;
  return (
    <div className="overflow-x-auto pb-1" data-rb="time-rows" aria-label={title} role="group">
      <div className={scroll ? 'min-w-max' : undefined}>
        {table.rows.map((row) => (
          <div key={row.key} data-rb-strip={row.key} className="flex items-end gap-2 border-b border-gray-100 py-1.5 last:border-0 dark:border-gray-700">
            <div
              className={`${labelCell} ${quiet(row) ? 'italic text-gray-500 dark:text-gray-400' : 'font-medium text-gray-700 dark:text-gray-200'}`}
              title={row.label}
            >
              {row.label}
            </div>
            <ul aria-label={row.label} className="flex flex-1 items-end gap-1">
              {row.cells.map((cell, j) => {
                const col = table.columns[j];
                const words = `${col ? col.long : ''}: ${numberText(cell.value, table.unit)}`;
                return (
                  <li key={col ? col.key : j} aria-label={words} title={words} className={`flex h-10 flex-col justify-end ${width}`}>
                    <div
                      className={`mx-auto w-full max-w-[2.5rem] rounded-t-sm ${colors.get(row.key) ?? OTHER_COLOR}`}
                      style={{ height: `${cell.pct}%` }}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <div className="flex gap-2 pt-1" aria-hidden="true">
          <div className="w-32 flex-shrink-0 sm:w-40" />
          <div className="flex flex-1 gap-1">
            {table.columns.map((col, j) => (
              <span
                key={col.key}
                className={`whitespace-nowrap text-center text-[9px] leading-none text-gray-400 dark:text-gray-500 ${width} ${
                  j % step === 0 ? '' : 'invisible'
                }`}
              >
                {col.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        One lead can count in more than one row here, so each row has its own bars rather than one stacked total.
      </p>
    </div>
  );
}

/** Bars over time; renders nothing for a result that is not split by time. */
export default function TimeBars({ table, title }: TimeBarsProps) {
  if (table.columnAxis.kind !== 'time' || table.columns.length === 0) return null;
  const colors = seriesColors(table);
  return (
    <div data-rb="time-chart">
      {stackable(table) ? (
        <>
          <Stacked table={table} title={title} colors={colors} />
          <Legend table={table} colors={colors} />
        </>
      ) : (
        <Rows table={table} title={title} colors={colors} />
      )}
    </div>
  );
}
